/** @file Allocates one empty overlay RenderBuffers chunk with a fixed capacity (never grows). */
import { RENDER_LAYOUT } from '@pose2d/geometry/renderBuffers';
import type { RenderBuffers } from '@pose2d/geometry/types';
import { invariant } from '@pose2d/std/invariant';
import { OVERLAY_CHUNK_DEFAULTS } from './overlayChunkDefaults.data';

/** Capacity of one chunk; omitted fields use the defaults of createOverlayChunk. */
export interface OverlayChunkCapacity {
  /** 64..65535 (Uint16 indices); default 16384. */
  vertices?: number;
  /** At least 192 (the largest primitive, a ring); default 3 per vertex. */
  indices?: number;
  /** At least 2; default 64. */
  draws?: number;
}

/** A chunk for `OverlayInput.allocate`: zero counts, no runs, its own vertex ArrayBuffer. */
export function createOverlayChunk(capacity: OverlayChunkCapacity = {}): RenderBuffers {
  const vertices = capacity.vertices ?? OVERLAY_CHUNK_DEFAULTS.vertices;
  const indices = capacity.indices ?? vertices * OVERLAY_CHUNK_DEFAULTS.indicesPerVertex;
  const draws = capacity.draws ?? OVERLAY_CHUNK_DEFAULTS.draws;
  invariant(Number.isInteger(vertices) && vertices >= 64 && vertices <= RENDER_LAYOUT.MAX_VERTICES, 'overlay chunk vertices must be an integer in [64, 65535]');
  invariant(Number.isInteger(indices) && indices >= 192, 'overlay chunk indices must be an integer >= 192');
  invariant(Number.isInteger(draws) && draws >= 2, 'overlay chunk draws must be an integer >= 2');
  const buffer = new ArrayBuffer(vertices * RENDER_LAYOUT.VERTEX_WORDS * 4);
  return {
    vertexF32: new Float32Array(buffer),
    vertexU32: new Uint32Array(buffer),
    indices: new Uint16Array(indices),
    draws: new Int32Array(draws * RENDER_LAYOUT.DRAW_STRIDE),
    runs: new Int32Array(0),
    vertexCount: 0,
    indexCount: 0,
    drawCount: 0,
    runCount: 0,
    version: 0,
    topologyVersion: 0,
    bounds: new Float32Array(4),
  };
}
